import { useEffect, useMemo, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { DataGrid, type GridColDef, type GridSelectionModel } from '@mui/x-data-grid';

import useUserCourses from 'Hooks/useUserCourses';

const columns: GridColDef[] = [
  { field: 'id', headerName: '', width: 90, hide: true, flex: 1 },
  { field: 'name', headerName: 'Nombre', flex: 1 },
];

interface CourseAssignModalProps {
  userId: string;
  visible?: boolean;
  onClose?: () => void;
  onAssign?: (courseIds: string[]) => void;
}

const CourseAssignModal = ({ userId, visible = false, onClose, onAssign }: CourseAssignModalProps) => {
  const [selectedCourseIds, setSelectedCourseIds] = useState<GridSelectionModel>([]);
  const [filter, setFilter] = useState('');

  const { courses, fetch } = useUserCourses({
    init: true,
    userId,
    assigned: false,
  });

  useEffect(() => {
    fetch(userId);
  }, [visible, fetch, userId]);

  const rows = useMemo(() => {
    if (!courses) return [];
    const normalizedFilter = filter.toLowerCase();
    return courses
      .filter(course => course.Course.name.toLowerCase().includes(normalizedFilter))
      .map(course => ({
        id: course.Course.id,
        name: course.Course.name,
        description: course.Course.description,
      }));
  }, [courses, filter]);

  const handleAssign = () => {
    if (onAssign) {
      onAssign(selectedCourseIds as string[]);
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedCourseIds([]);
    setFilter('');
    fetch(userId);
    onClose?.();
  };

  return (
    <Dialog open={visible} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Asignar cursos</DialogTitle>
      <DialogContent sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
        <TextField
          label="Buscar curso"
          variant="outlined"
          size="small"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection
          hideFooterPagination
          selectionModel={selectedCourseIds}
          onSelectionModelChange={(newSelection: GridSelectionModel) => setSelectedCourseIds(newSelection)}
        />
      </DialogContent>
      <DialogActions sx={{ padding: '1.5rem' }}>
        <Button onClick={handleClose} color="primary" variant="outlined">
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleAssign} disabled={selectedCourseIds.length === 0} color="success">
          Asignar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourseAssignModal;
